import { useMemo, useState } from 'react'
import { Icon } from '../Icon'
import { useLiveSignOn, useLiveSignOff } from '../../hooks/useLivePulse'
import type {
  LivePulsePayload,
  LiveTimelineItem,
  LiveMyDuty,
  LiveShift
} from '../../types'

// LiveMyDuties — volunteer / personal-view body inside LiveDayView
// (Live View PR L4 / spec 33 §6, prototype L2425-2545).
//
// Layout:
//   Hero card — brass-tinted gradient with avatar, role name, shift
//     time, sign-on/off button. Acts as the "NEXT UP" affordance per
//     the work plan's L4 acceptance criteria.
//   Below — 2-col grid (1.4fr / 1fr):
//     Left:  Your Tasks Today — duties as tickable rows
//     Right: Need Help? (Call Duty Manager + Quick Reference) +
//            Today's Services mini-list
//
// Toast strings (caller-managed):
//   - "Signed on — welcome!" (literal em-dash)
//   - "Signed off — your time has been logged"
//
// Volunteers and admin both see this view via the View toggle (admin)
// or directly (volunteers — server hides the Manager Overview tab).

interface Props {
  eventId: number
  pulse: LivePulsePayload
  meDbUserId: number | null
  onShowResources: () => void
  showToast: (msg: string) => void
}

export function LiveMyDuties({
  eventId,
  pulse,
  meDbUserId,
  onShowResources,
  showToast
}: Props) {
  const signOnMut = useLiveSignOn(eventId)
  const signOffMut = useLiveSignOff(eventId)
  const [tasksDone, setTasksDone] = useState<Set<number>>(new Set())

  const myShift = useMemo<LiveShift | null>(() => {
    if (meDbUserId == null) return null
    // Prefer an open shift; fall back to the most-recent signed-off row.
    const open = pulse.shifts.find((s) => s.userId === meDbUserId && s.signedOffAt == null)
    if (open) return open
    const closed = pulse.shifts
      .filter((s) => s.userId === meDbUserId && s.signedOffAt != null)
      .sort((a, b) => (b.signedOffAt ?? '').localeCompare(a.signedOffAt ?? ''))
    return closed[0] ?? null
  }, [pulse.shifts, meDbUserId])

  const isOn = myShift?.signedOnAt != null && myShift?.signedOffAt == null
  const myDuties = pulse.myDuties

  // Derive "what's on now" from the first duty of the day. For volunteers
  // there's typically one slot per day — multi-slot rosters surface as
  // multiple rows in the Tasks list.
  const primaryDuty = myDuties[0] ?? null
  const shiftWindow = primaryDuty
    ? formatShiftWindow(primaryDuty)
    : 'No assignment for today'

  const pending = signOnMut.isPending || signOffMut.isPending
  function toggleSign() {
    if (pending) return
    if (isOn) {
      signOffMut.mutate(undefined, {
        onSuccess: () => showToast('Signed off — your time has been logged')
      })
    } else {
      signOnMut.mutate(undefined, {
        onSuccess: () => showToast('Signed on — welcome!')
      })
    }
  }

  function toggleTask(id: number) {
    setTasksDone((cur) => {
      const next = new Set(cur)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="space-y-4">
      {/* Hero card — brass-tinted gradient per prototype L2443-2457 */}
      <div
        className="day-card"
        style={{
          background:
            'linear-gradient(135deg, rgba(155, 127, 42, 0.18) 0%, rgba(155, 127, 42, 0.08) 100%)',
          borderColor: 'rgba(155, 127, 42, 0.45)'
        }}
      >
        <div
          className="flex items-center"
          style={{ gap: 14, flexWrap: 'wrap' }}
        >
          <div
            style={{
              width: 50,
              height: 50,
              borderRadius: '50%',
              background: 'rgba(155, 127, 42, 0.4)',
              color: '#fdfbf6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              fontWeight: 700,
              flexShrink: 0
            }}
            aria-hidden
          >
            {/* Use the first myDuty's role first-letters as the placeholder
                avatar — real avatar comes via useMe.name in a later batch. */}
            {primaryDuty ? avatarFromRole(primaryDuty.roleName) : '—'}
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div
              style={{
                fontSize: 11,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'rgba(245, 241, 234, 0.6)',
                fontWeight: 600
              }}
            >
              Your Shift Today
            </div>
            <div
              className="font-display"
              style={{ fontSize: 22, fontWeight: 600, color: '#fdfbf6', lineHeight: 1.15 }}
            >
              {primaryDuty ? primaryDuty.roleName : 'Off duty'}
            </div>
            <div className="text-sm" style={{ color: 'rgba(245, 241, 234, 0.75)' }}>
              {shiftWindow}
            </div>
            {isOn && myShift?.signedOnAt && (
              <div className="text-xs mt-1" style={{ color: '#80e8a8' }}>
                <Icon name="check-circle" size={12} /> Signed on at {formatClockTime(myShift.signedOnAt)}
              </div>
            )}
            {myShift?.signedOffAt && (
              <div className="text-xs mt-1" style={{ color: 'rgba(245, 241, 234, 0.55)' }}>
                Signed off at {formatClockTime(myShift.signedOffAt)}
              </div>
            )}
          </div>
          {meDbUserId != null && (
            <button
              type="button"
              onClick={toggleSign}
              disabled={pending}
              className="inline-flex items-center gap-2 text-sm font-semibold rounded-md transition-colors disabled:opacity-60"
              style={{
                minWidth: 140,
                justifyContent: 'center',
                padding: '10px 18px',
                background: isOn ? '#7a2329' : '#9b7f2a',
                border: `1px solid ${isOn ? '#7a2329' : '#9b7f2a'}`,
                color: '#fff'
              }}
            >
              <Icon name={isOn ? 'lock' : 'check-circle'} size={15} />
              {pending ? '…' : isOn ? 'Sign Off' : 'Sign On'}
            </button>
          )}
        </div>
        {signOnMut.isError && (
          <div className="text-xs mt-3" style={{ color: '#ef9a9a' }}>
            Sign-on failed: {(signOnMut.error as Error).message}
          </div>
        )}
        {signOffMut.isError && (
          <div className="text-xs mt-3" style={{ color: '#ef9a9a' }}>
            Sign-off failed: {(signOffMut.error as Error).message}
          </div>
        )}
      </div>

      {/* Body grid — Tasks Today (left) + Need Help / Services (right) */}
      <div
        className="grid"
        style={{ gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)', gap: 16 }}
        data-live-grid="my-duties"
      >
        <TasksTodayCard duties={myDuties} done={tasksDone} onToggle={toggleTask} />

        <div className="space-y-4">
          <NeedHelpCard onOpenResources={onShowResources} />
          <TodaysServicesCard items={pulse.timeline} />
        </div>
      </div>
    </div>
  )
}

// ---- Tasks Today ----------------------------------------------------------

function TasksTodayCard({
  duties,
  done,
  onToggle
}: {
  duties: LiveMyDuty[]
  done: Set<number>
  onToggle: (id: number) => void
}) {
  return (
    <div className="day-card">
      <div className="day-card-head">Your Tasks Today</div>
      {duties.length === 0 && (
        <div className="text-sm" style={{ color: 'rgba(245, 241, 234, 0.55)' }}>
          No assignments today. Catch your breath.
        </div>
      )}
      {duties.map((duty, i) => {
        const isDone = done.has(duty.assignmentId)
        const window = formatShiftWindow(duty)
        return (
          <div
            key={duty.assignmentId}
            onClick={() => onToggle(duty.assignmentId)}
            role="checkbox"
            aria-checked={isDone}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onToggle(duty.assignmentId)
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 0',
              borderBottom: i === duties.length - 1 ? 'none' : '1px solid rgba(245, 241, 234, 0.1)',
              cursor: 'pointer'
            }}
          >
            <div
              aria-hidden
              style={{
                width: 22,
                height: 22,
                borderRadius: 5,
                background: isDone ? '#0a4a3a' : 'rgba(245, 241, 234, 0.05)',
                borderColor: isDone ? '#0a4a3a' : 'rgba(245, 241, 234, 0.2)',
                borderStyle: 'solid',
                borderWidth: 1,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 13,
                flexShrink: 0
              }}
            >
              {isDone ? '✓' : ''}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                className="text-sm font-semibold"
                style={{
                  color: isDone ? 'rgba(245, 241, 234, 0.5)' : '#fdfbf6',
                  textDecoration: isDone ? 'line-through' : 'none'
                }}
              >
                {duty.roleName}
              </div>
              <div className="text-xs" style={{ color: 'rgba(245, 241, 234, 0.6)' }}>
                {window}
                {duty.note ? ` · ${duty.note}` : ''}
              </div>
            </div>
          </div>
        )
      })}
      {duties.length > 0 && (
        <div className="text-xs mt-2" style={{ color: 'rgba(245, 241, 234, 0.5)' }}>
          {done.size} of {duties.length} complete
        </div>
      )}
    </div>
  )
}

// ---- Need Help -------------------------------------------------------------

function NeedHelpCard({ onOpenResources }: { onOpenResources: () => void }) {
  return (
    <div className="day-card">
      <div className="day-card-head">Need Help?</div>
      <div className="text-sm" style={{ color: 'rgba(245, 241, 234, 0.75)', marginBottom: 14 }}>
        Tap to reach your duty manager or open quick reference.
      </div>
      <a
        href="tel:01947895468"
        className="w-full mb-2 inline-flex items-center justify-center gap-2 text-sm font-semibold rounded-md"
        style={{
          background: '#0a4a3a',
          color: '#fff',
          border: '1px solid #0a4a3a',
          padding: '10px 14px',
          textDecoration: 'none'
        }}
      >
        <Icon name="phone" size={16} /> Call Duty Manager
      </a>
      <button
        type="button"
        onClick={onOpenResources}
        className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold rounded-md"
        style={{
          background: 'rgba(245, 241, 234, 0.08)',
          color: '#fdfbf6',
          border: '1px solid rgba(245, 241, 234, 0.2)',
          padding: '10px 14px'
        }}
      >
        <Icon name="book-open" size={16} /> Quick Reference
      </button>
    </div>
  )
}

// ---- Today's Services -----------------------------------------------------

function TodaysServicesCard({ items }: { items: LiveTimelineItem[] }) {
  const shown = items.slice(0, 6)
  return (
    <div className="day-card">
      <div className="day-card-head">Today's Services</div>
      {shown.length === 0 && (
        <div className="text-sm" style={{ color: 'rgba(245, 241, 234, 0.55)' }}>
          No services scheduled today.
        </div>
      )}
      {shown.map((s, i) => (
        <div
          key={s.id}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '6px 0',
            borderBottom: i === shown.length - 1 ? 'none' : '1px solid rgba(245, 241, 234, 0.08)'
          }}
        >
          <span className="font-mono text-sm" style={{ color: '#fdfbf6' }}>{s.time}</span>
          <span className="text-xs" style={{ color: 'rgba(245, 241, 234, 0.6)' }}>
            {s.booked}/{s.capacity ?? '—'} on board
          </span>
        </div>
      ))}
    </div>
  )
}

// ---- helpers --------------------------------------------------------------

function formatShiftWindow(duty: LiveMyDuty): string {
  if (!duty.timeStart && !duty.timeEnd) return 'All-day shift'
  if (duty.timeStart && duty.timeEnd) return `${duty.timeStart} – ${duty.timeEnd}`
  return duty.timeStart ?? duty.timeEnd ?? ''
}

function formatClockTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '--:--'
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
}

function avatarFromRole(roleName: string): string {
  const parts = roleName.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}
